<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { I18nT } from 'vue-i18n'
import { InfoIcon } from '@ecx/ui/icons'
import { useBulkImportStore } from '@/stores/bulkImport'

const props = withDefaults(
  defineProps<{
    maxUsers?: string
    maxSize?: string
  }>(),
  {
    maxUsers: '10.000',
    maxSize: '20 MB',
  },
)

const { t } = useI18n()
const store = useBulkImportStore()
const isVehicles = computed(() => store.entity === 'vehicles')
const isCards = computed(() => store.entity === 'cards')
const isEdit = computed(() => store.mode === 'edit')
const entityLabel = computed(() => {
  if (isCards.value) return t('corp.bulk_import.edit.upload.entity_label_cards')
  return isVehicles.value
    ? t('corp.bulk_import.edit.upload.entity_label_vehicles')
    : t('corp.bulk_import.edit.upload.entity_label_users')
})

const limitsStrongText = computed(() => {
  const max = props.maxUsers
  if (isCards.value) return t('corp.bulk_import.step_selector.instructions_2_strong_users_cards', { max })
  return isVehicles.value
    ? t('corp.bulk_import.step_selector.instructions_2_strong_users_vehicles', { max })
    : t('corp.bulk_import.step_selector.instructions_2_strong_users', { max })
})
</script>

<template>
  <div class="mx-6 mt-4 p-4 bg-grey-50 rounded-lg border border-grey-50 flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <InfoIcon class="w-5 h-5 text-grey-600 shrink-0" aria-hidden="true" />
      <span class="text-sm text-grey-950">{{ t('corp.bulk_import.step_selector.instructions_title') }}</span>
    </div>
    <ul class="text-label-xs text-grey-600 space-y-2 list-disc list-inside">
      <li>
        {{ isEdit
            ? t('corp.bulk_import.edit.upload.instructions_1', { entityLabel })
            : t('corp.bulk_import.step_selector.instructions_1') }}
      </li>
      <li>
        <I18nT keypath="corp.bulk_import.step_selector.instructions_2" tag="span" scope="global">
          <template #strongUsers>
            <strong class="font-bold">{{ limitsStrongText }}</strong>
          </template>
          <template #strongSize>
            <strong class="font-bold">{{ t('corp.bulk_import.step_selector.instructions_2_strong_size', { size: maxSize }) }}</strong>
          </template>
        </I18nT>
      </li>
      <li v-if="!isEdit">{{ t('corp.bulk_import.step_selector.instructions_3') }}</li>
    </ul>
  </div>
</template>
